import sharedSupabase from '../config/supabase';

/**
 * Asks the server to re-resolve one relationship's price comparison (the
 * items-ai-match edge function runs the deterministic pass and falls back to
 * ONE AI call only when a package stays uninterpretable — see
 * supabase/functions/_shared/priceComparison.mjs).
 *
 * Fire-and-forget by design: comparison freshness is a nicety, and no option
 * write, merge, or shopping trip may fail because this round trip did. Every
 * failure degrades to a console.warn; callers never await the result unless
 * they want the fresh resolution for immediate display.
 */
export async function requestComparisonRecompute(storeId, inventoryItemId, client = sharedSupabase) {
  if (!storeId || inventoryItemId == null || !client?.functions?.invoke) return null;
  try {
    const { data, error } = await client.functions.invoke('items-ai-match', {
      body: {
        action: 'objective_prices',
        store_id: storeId,
        inventory_item_id: inventoryItemId,
      },
    });
    if (error) throw error;
    return data ?? null;
  } catch (err) {
    console.warn('comparison recompute skipped', inventoryItemId, err?.message || err);
    return null;
  }
}

/**
 * Nudges the server to drain the group-comparison trigger-outbox
 * (items-ai-match action 'drain_group_comparisons'). Relationship mutations
 * enqueue group recompute requests via database triggers; this call only
 * shortens the wait until the next scheduled sweep would drain them, so the
 * user who just confirmed an equivalence sees the group's comparison sooner.
 *
 * Latency nicety ONLY — the triggers plus the scheduler sweep are the
 * correctness path. Same fire-and-forget contract as
 * requestComparisonRecompute: every failure degrades to a console.warn and
 * no mutation may fail because this round trip did.
 */
export async function requestGroupComparisonDrain(client = sharedSupabase) {
  if (!client?.functions?.invoke) return null;
  try {
    const { data, error } = await client.functions.invoke('items-ai-match', {
      body: { action: 'drain_group_comparisons' },
    });
    if (error) throw error;
    return data ?? null;
  } catch (err) {
    console.warn('group comparison drain skipped', err?.message || err);
    return null;
  }
}

/**
 * Recompute ONE equivalent group's comparison now, and wait for it.
 *
 * requestGroupComparisonDrain only drains work the database triggers already
 * enqueued, so it does nothing at all for a group whose membership has not
 * changed — which is exactly the case the Related Products editor's
 * "Recalculate group" button exists for. This calls the SAME server pass the
 * drain runs (items-ai-match action 'group_comparison' →
 * processGroupComparison → persistGroupComparison → equivalent_group_comparisons),
 * addressed at specific anchors instead of at the queue. No second comparison
 * implementation, and no browser-side price maths.
 *
 * Every anchor is resolved to its CURRENT group server-side and the groups are
 * deduplicated there, so passing every member of a group that just lost a
 * member recomputes both the surviving group and the departed item's new
 * situation in one call. A group left with fewer than two live members has its
 * stale record deleted and comes back under `dissolved_anchor_item_ids`.
 *
 * Unlike its fire-and-forget neighbours this one is meant to be awaited: the
 * user is watching a "Recalculating prices…" line. It still never throws —
 * a null return means "the comparison did not refresh", never a corrupted
 * group, and the caller decides what to say about it.
 */
export async function requestGroupComparison(storeId, inventoryItemIds = [], client = sharedSupabase) {
  const itemIds = [...new Set((inventoryItemIds || [])
    .map(Number).filter((id) => Number.isFinite(id) && id > 0))];
  if (!storeId || itemIds.length === 0 || !client?.functions?.invoke) return null;
  try {
    const { data, error } = await client.functions.invoke('items-ai-match', {
      body: {
        action: 'group_comparison',
        store_id: storeId,
        item_ids: itemIds,
      },
    });
    if (error) throw error;
    return data ?? null;
  } catch (err) {
    console.warn('group comparison recompute skipped', itemIds, err?.message || err);
    return null;
  }
}

/**
 * Compare complete Shopping Review candidate sets: Equivalent primaries plus
 * direct confirmed Alternative closures. The server resolves membership and
 * returns a review-only snapshot; it never persists substitute measurements
 * over either product's Equivalent-group comparison.
 */
export async function requestShoppingComparisons(
  storeId,
  inventoryItemIds = [],
  client = sharedSupabase,
) {
  const itemIds = [...new Set((inventoryItemIds || [])
    .map(Number).filter((id) => Number.isFinite(id) && id > 0))];
  if (!storeId || itemIds.length === 0 || !client?.functions?.invoke) return null;
  try {
    const { data, error } = await client.functions.invoke('items-ai-match', {
      body: {
        action: 'shopping_comparison',
        store_id: storeId,
        item_ids: itemIds,
      },
    });
    if (error) throw error;
    return data ?? null;
  } catch (err) {
    console.warn('shopping comparison skipped', err?.message || err);
    return null;
  }
}

/** Recompute several relationships, sequentially to stay gentle on the API. */
export async function requestComparisonRecomputeForItems(storeId, inventoryItemIds = [], client = sharedSupabase) {
  const unique = [...new Set((inventoryItemIds || []).filter((id) => id != null))];
  const results = [];
  for (const itemId of unique) {
    // eslint-disable-next-line no-await-in-loop
    results.push(await requestComparisonRecompute(storeId, itemId, client));
  }
  return results;
}
